// Lists every plan-level and day-level share where the signed-in household
// is the RECIPIENT (see db.js's "shared with us" section). Deliberately a
// separate page from PlanPage rather than a switcher on the current week --
// plansByWeek there is keyed only by week_start and assumes exactly one
// plan per week (yours), so another household's plan for the same calendar
// week has no slot to merge into; navigating "into" it via this page's own
// expandable rows is how that's resolved instead.
//
// Expanding a row mounts SharedWeekEditor/SharedDayEditor (piece C) rather
// than the earlier read-only WeekPlanPreview/WeekPlanDayPreview -- those
// stay in use only for the public token-based link pages, which are
// read-only by design; a household-to-household share always implies full
// edit collaboration, so once real editing existed there was no reason to
// keep showing a preview here.
import { useEffect, useState } from "react";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { formatWeekRange } from "../lib/utils";
import { loadSharedPlansWithUs, loadSharedDaysWithUs, getWeekPlanSharedBy, getWeekPlanDaySharedBy } from "../lib/db";
import { SharedWeekEditor, SharedDayEditor } from "../components/SharedWeekEditor";
import { COLORS, FONT_SIZE, SPACE, RADIUS } from "../lib/constants";

export function SharedWithUsPage() {
  const { householdId, colors } = useMealPlanner();
  const { card, soft, accent, line } = colors;
  const [plans, setPlans] = useState([]);
  const [days, setDays] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [sharedByPlan, setSharedByPlan] = useState({});
  const [sharedByDay, setSharedByDay] = useState({});
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadSharedPlansWithUs(householdId), loadSharedDaysWithUs(householdId)])
      .then(async ([planRows, dayRows]) => {
        if (cancelled) return;
        setPlans(planRows);
        setDays(dayRows);
        const planEmails = {};
        await Promise.all(
          planRows.map(async (p) => {
            planEmails[`${p.household_id}|${p.week_start}`] = await getWeekPlanSharedBy(p.household_id, p.week_start).catch(() => "");
          })
        );
        const dayEmails = {};
        await Promise.all(
          dayRows.map(async (d) => {
            dayEmails[d.week_plan_day_id] = await getWeekPlanDaySharedBy(d.week_plan_day_id).catch(() => "");
          })
        );
        if (!cancelled) {
          setSharedByPlan(planEmails);
          setSharedByDay(dayEmails);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  if (!loaded) {
    return (
      <div className="mp-sans" style={{ color: soft, padding: SPACE.x3 }}>
        Loading…
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)", marginBottom: SPACE.x3 }}>
        <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg, marginBottom: SPACE.xs }}>Shared weeks</div>
        <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: "0 0 var(--space-xl)" }}>
          Whole weeks other households have shared with you — you can view and edit these together.
        </p>
        {plans.length === 0 && (
          <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: 0 }}>
            No one has shared a week with you yet.
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          {plans.map((p) => {
            const key = `${p.household_id}|${p.week_start}`;
            const expanded = expandedPlan === key;
            const sections = [p.include_snacks && "snacks", p.include_preserves && "preserves", p.include_medicinal && "medicinal"].filter(Boolean);
            return (
              <div key={p.id} style={{ border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl2)" }}>
                <div className="mp-sans" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.lg, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: FONT_SIZE.md }}>Week of {formatWeekRange(p.week_start)}</div>
                    <div style={{ fontSize: FONT_SIZE.sm, color: soft }}>
                      Shared by {sharedByPlan[key] || "another household"}
                      {sections.length > 0 && ` — includes ${sections.join(", ")}`}
                    </div>
                  </div>
                  <button
                    className="mp-btn"
                    onClick={() => setExpandedPlan(expanded ? null : key)}
                    style={{ background: expanded ? accent : "transparent", border: `1px solid ${expanded ? accent : line}`, color: expanded ? COLORS.onAccent : accent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
                  >
                    {expanded ? "Hide" : "View & edit"}
                  </button>
                </div>
                {expanded && (
                  <div style={{ marginTop: SPACE.xl, borderTop: `1px dashed ${line}`, paddingTop: SPACE.xl }}>
                    <SharedWeekEditor
                      householdId={p.household_id}
                      weekStart={p.week_start}
                      sections={{ includeSnacks: p.include_snacks, includePreserves: p.include_preserves, includeMedicinal: p.include_medicinal }}
                      colors={colors}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)" }}>
        <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg, marginBottom: SPACE.xs }}>Shared days</div>
        <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: "0 0 var(--space-xl)" }}>
          Single days other households have shared with you — you can view and edit these together.
        </p>
        {days.length === 0 && (
          <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: 0 }}>
            No one has shared a day with you yet.
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          {days.map((d) => {
            const wpd = d.week_plan_days;
            if (!wpd) return null;
            const key = d.week_plan_day_id;
            const expanded = expandedDay === key;
            return (
              <div key={d.id} style={{ border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl2)" }}>
                <div className="mp-sans" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.lg, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: FONT_SIZE.md }}>
                      {wpd.day}, week of {formatWeekRange(wpd.week_start)}
                    </div>
                    <div style={{ fontSize: FONT_SIZE.sm, color: soft }}>Shared by {sharedByDay[key] || "another household"}</div>
                  </div>
                  <button
                    className="mp-btn"
                    onClick={() => setExpandedDay(expanded ? null : key)}
                    style={{ background: expanded ? accent : "transparent", border: `1px solid ${expanded ? accent : line}`, color: expanded ? COLORS.onAccent : accent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
                  >
                    {expanded ? "Hide" : "View & edit"}
                  </button>
                </div>
                {expanded && (
                  <div style={{ marginTop: SPACE.xl, borderTop: `1px dashed ${line}`, paddingTop: SPACE.xl }}>
                    <SharedDayEditor householdId={wpd.household_id} weekStart={wpd.week_start} day={wpd.day} colors={colors} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
